import axios from "axios";
import { getSession } from "@/lib/authApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Attach the session Bearer token on every request. The token is minted on
// PIN/WebAuthn pairing and lives in localStorage — see lib/authApi.js.
axios.interceptors.request.use((config) => {
  // Only auto-attach for requests that hit our own backend. External URLs
  // (Stripe, Brevo, etc.) must not receive our token.
  if (typeof config.url === "string" && config.url.startsWith(BACKEND_URL)) {
    const s = getSession();
    if (s?.token) {
      config.headers = config.headers || {};
      // Don't overwrite an explicit Authorization header the caller has set.
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${s.token}`;
      }
    }
  }
  return config;
});

// If the backend reports the session as dead (expired, invalid, or the
// underlying license was revoked by an admin), immediately sign the user
// out so they can't keep clicking around a broken UI. AuthGate listens for
// this event and drops them back to Login.
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      const detail = err?.response?.data?.detail;
      if (["LICENSE_REVOKED", "INVALID_TOKEN", "NO_SESSION",
           "EXPIRED_TOKEN"].includes(detail)) {
        try {
          window.dispatchEvent(new Event("authSignOut"));
        } catch {
          // window may not be defined in SSR / tests — ignore.
        }
      }
    }
    return Promise.reject(err);
  }
);

// Background-job upload flow (fixes recurring Cloudflare 520 on prod):
//   1. POST /parse-report/start → returns { job_id } within ~1s of upload
//      finishing (no inline parse, no CF edge timeout exposure).
//   2. Poll GET /parse-report/status/{job_id} every POLL_INTERVAL_MS until
//      `status === "done" | "error"`.
//   3. GET /parse-report/result/{job_id} returns the parsed ReportAnalysis.
// The `onProgress` callback receives 0-100 for upload progress, then `null`
// while parsing (caller can show an indeterminate UI).
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 5 * 60 * 1000; // 5 minutes — matches old client timeout

async function pollUntilDone(jobId, signal) {
  const started = Date.now();
  while (true) {
    if (signal?.aborted) throw new Error("Upload cancelled");
    if (Date.now() - started > MAX_POLL_MS) {
      throw new Error("Parse timed out after 5 minutes");
    }
    const res = await axios.get(`${API}/parse-report/status/${jobId}`, {
      timeout: 15000,
    });
    const status = res.data?.status;
    if (status === "done") return;
    if (status === "error") {
      throw new Error(res.data?.error || "Parse failed");
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
}

export async function uploadAndParse(file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  // Step 1 — upload bytes + queue the parse job.
  const startRes = await axios.post(`${API}/parse-report/start`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
    onUploadProgress: (evt) => {
      if (!onProgress || !evt.total) return;
      const pct = Math.round((evt.loaded / evt.total) * 100);
      onProgress(pct);
    },
  });
  const jobId = startRes.data?.job_id;
  if (!jobId) throw new Error("Backend did not return a job_id");
  // Step 2 — flip the UI into "parsing" mode while we poll.
  if (onProgress) onProgress(null);
  await pollUntilDone(jobId);
  // Step 3 — fetch the parsed analysis.
  const resultRes = await axios.get(`${API}/parse-report/result/${jobId}`, {
    timeout: 60000,
  });
  return resultRes.data;
}

export async function uploadRebatePdf(file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API}/parse-rebate-report`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
    onUploadProgress: (evt) => {
      if (!onProgress || !evt.total) return;
      onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  return res.data;
}

// Patient preferences — per-license Type + Bridging metadata attached to
// each patient in the ODB / NIHB drill-down. Persisted server-side so
// tags follow the patient across every future PDF upload.
export async function fetchPatientPreferences() {
  const res = await axios.get(`${API}/patient-preferences`, { timeout: 15000 });
  return res.data?.items || [];
}

export async function savePatientPreference({ patient, type, bridging, recovered, program }) {
  const res = await axios.post(
    `${API}/patient-preferences`,
    {
      patient,
      type: type || "",
      bridging: bridging || "",
      recovered: recovered || "",
      program: program || "",
    },
    { timeout: 15000 },
  );
  return res.data;
}

export async function fetchCyclePatientTags() {
  const res = await axios.get(`${API}/cycle-patient-tags`, { timeout: 15000 });
  return res.data?.items || [];
}

export async function saveCyclePatientTag({ scope, cycle_key, patient, type, bridging, recovered }) {
  const body = { scope, cycle_key, patient };
  if (type !== undefined) body.type = type || "";
  if (bridging !== undefined) body.bridging = bridging || "";
  if (recovered !== undefined) body.recovered = recovered || "";
  const res = await axios.post(`${API}/cycle-patient-tags`, body, { timeout: 15000 });
  return res.data;
}

export async function uploadBirthdayPdf(file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API}/parse-birthday-list`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
    onUploadProgress: (evt) => {
      if (!onProgress || !evt.total) return;
      onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  return res.data;
}
